import { GridRowId } from '@mui/x-data-grid-pro';
import { TEDFormatTimeMinute } from '../../../../../../Utils/Formatters/FormatterClasses/TEDFormatTimeMinute';
import { ParseResult } from '../../../../../../Utils/Formatters/FormatterTypes';
import {
  getFieldValidationErrorMessage,
  getFieldValidationWarningMessage,
  IFieldValidations,
} from '../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';

interface IPlaneringTidValidationRow {
  id: GridRowId;
  isEmptyPlaceholder?: boolean;
  FRANTID?: string | null;
  TILLTID?: string | null;
}

const timeMinuteFormatter = new TEDFormatTimeMinute();

const getRowFieldValidations = (
  validations: IFieldValidations | undefined,
  rowId: GridRowId,
  fieldName: string
): IFieldValidations =>
  validations?.filter(
    validation =>
      validation.fieldName === fieldName &&
      validation.path?.includes(String(rowId))
  ) ?? [];

export const getPlaneringFieldWarningMessage = (
  validations: IFieldValidations | undefined,
  rowId: GridRowId,
  fieldName: 'FRANTID' | 'TILLTID'
): string | undefined => {
  const rowFieldValidations = getRowFieldValidations(
    validations,
    rowId,
    fieldName
  );
  return (
    getFieldValidationErrorMessage(rowFieldValidations, fieldName) ??
    getFieldValidationWarningMessage(rowFieldValidations, fieldName)
  );
};

const parseAndFormatTid = (value: string): string | null => {
  const trimmedValue = value.trim();
  if (trimmedValue.length === 0) return '';

  const parsed = timeMinuteFormatter.parser.parse(trimmedValue);
  if (parsed.result !== ParseResult.OK) {
    return null;
  }

  return timeMinuteFormatter.displayer.display(parsed.value, true);
};

export const normalizeTidInput = (value: string): string => {
  const parsed = parseAndFormatTid(value);
  return parsed === null ? value.trim() : parsed;
};

export const getInvalidTidMessage = (
  value: string | null | undefined
): string | undefined => {
  if (!value) return undefined;
  return parseAndFormatTid(value) === null ? 'Ogiltig tid' : undefined;
};

export const validatePlaneringTidRows = (
  currentValues: unknown
): IFieldValidations => {
  if (!Array.isArray(currentValues)) {
    return [];
  }

  return currentValues.reduce<IFieldValidations>((validations, row) => {
    const planeringRow = row as IPlaneringTidValidationRow;

    if (planeringRow.isEmptyPlaceholder) {
      return validations;
    }

    const franTidWarning = getInvalidTidMessage(planeringRow.FRANTID);
    if (franTidWarning) {
      validations.push({
        fieldName: 'FRANTID',
        message: franTidWarning,
        type: 'Warning',
        path: [String(planeringRow.id)],
      });
    }

    const tillTidWarning = getInvalidTidMessage(planeringRow.TILLTID);
    if (tillTidWarning) {
      validations.push({
        fieldName: 'TILLTID',
        message: tillTidWarning,
        type: 'Warning',
        path: [String(planeringRow.id)],
      });
    }

    return validations;
  }, []);
};
